import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { useShop } from "@/context/ShopContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FALLBACK_CATEGORIES = ["Mugs", "Glasses", "Salt & Pepper Sets", "Planters", "Decorative Pots", "Decor Items"];

const Shop = () => {
  const { products, loadingProducts, categories } = useShop();
  const CATEGORIES = ["All", ...(categories.length ? categories.map((x) => x.name) : FALLBACK_CATEGORIES)];
  const [params] = useSearchParams();
  const [cat, setCat] = useState(params.get("cat") || "All");
  const [q, setQ] = useState(params.get("q") || "");
  const [sort, setSort] = useState("featured");

  useEffect(() => {
    const qp = params.get("q");
    if (qp !== null) setQ(qp);
    const cp = params.get("cat");
    if (cp) setCat(cp);
  }, [params]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (cat !== "All") list = list.filter((p) => p.category === cat);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(needle) || p.category.toLowerCase().includes(needle));
    }
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest") list.sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    else list.sort((a, b) => Number(b.featured) - Number(a.featured));
    return list;
  }, [products, cat, q, sort]);

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-36" data-testid="shop-page">
      <Reveal>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">The full collection</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-espresso sm:text-4xl lg:text-5xl">Shop all ceramics</h1>
      </Reveal>

      <div className="mt-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="no-scrollbar flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`category-filter-item-${c.toLowerCase().replace(/[\s&]+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-200 ${
                cat === c ? "border-espresso bg-espresso text-cream" : "border-linen bg-white text-clay hover:border-terra hover:text-terra"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="relative flex-1 lg:w-64">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-clay/40" />
            <input
              data-testid="catalog-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search ceramics…"
              className="h-11 w-full rounded-full border border-linen bg-white pl-10 pr-4 text-sm outline-none transition-colors focus:border-terra"
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="h-11 w-44 rounded-full border-linen bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="featured">Curated</SelectItem>
              <SelectItem value="price-asc">Price: low to high</SelectItem>
              <SelectItem value="price-desc">Price: high to low</SelectItem>
              <SelectItem value="newest">Newest kiln drops</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loadingProducts ? (
        <div className="mt-16 grid grid-cols-2 gap-6 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-sand" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-24 text-center">
          <p className="font-serif text-2xl text-espresso">Nothing fired up here yet</p>
          <p className="mt-2 text-sm text-clay/60">Try another category or search term.</p>
        </div>
      ) : (
        <div className="mt-12 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4" data-testid="product-grid">
          {filtered.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i * 0.05, 0.3)}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default Shop;
